import { useState, type ReactNode } from "react";
import {
  Activity, Ban, BatteryLow, ChevronDown, Cpu, Crosshair, Flame, Gauge,
  Radio, ShieldAlert, Thermometer, ThermometerSnowflake, ThermometerSun,
  TrendingDown, TrendingUp, TriangleAlert, Wind, Zap, ZapOff,
} from "lucide-react";

import type { AssetDataBitfield } from "@/api/client";

/** The fault panel, ported from the cluster meter (clusterLive.css).
 *
 *  CATEGORY COLUMNS of icon-left cells, collapsed by default, with the active
 *  count in the header. An active condition gets a coloured border, icon and
 *  text and NOTHING else: that stylesheet says in as many words that the cell
 *  must not fill in and must not blink or glow.
 *
 *  AN ALARM AND A PROTECTION ARE THE SAME CONDITION AT TWO STAGES, which is
 *  Dattatray's rule: *"Alarms are early warning: so they should orange. If
 *  alarms goes into fault: Its red."* The firmware agrees -- the proto says
 *  `batt_alarm_bits` are the warnings that "precede the protections in
 *  battery_err_bits" -- and the names pair up: Cell High Voltage precedes Cell
 *  Over Voltage, Pack Low Voltage precedes Pack Under Voltage. So each
 *  condition is ONE cell that escalates, rather than the same fault appearing
 *  in two panels where a reader has to join them by eye.
 *
 *  SIX ALARMS HAVE NO PROTECTION and five protections have no alarm (Thermal
 *  Runaway, Short Circuit, both MOSFET failures, AFE comms). Those stay
 *  single-stage. Inventing the missing half of a pair would put a stage on
 *  screen the pack cannot report.
 *
 *  THE WHOLE CATALOGUE IS DRAWN, not just what is wrong. A panel listing only
 *  active faults cannot tell "nothing is wrong" from "this product declares no
 *  faults", and an engineer looking for a fault that is NOT firing needs to
 *  see that it exists.
 *
 *  THE NAMES COME FROM THE SERVER. `shared/lib/bitfields.py` holds the checked
 *  copy of the firmware's `bit_names` and the server sends {bit, name, on}.
 *  There is deliberately no decoder here: a third copy of a bit layout is how
 *  a renumbered bit ends up looking right on screen while naming the wrong
 *  fault.
 *
 *  A REGISTER THE DEVICE DOES NOT SEND IS ABSENT, not all-clear. "Every fault
 *  clear" and "we cannot see the fault register" are different answers.
 */

export type CellState = "fault" | "warn" | "clear" | "na";

export interface FaultCell {
  name: string;
  state: CellState;
  hint: string;
  /** Lifetime occurrences of THIS condition, from the pack's own 0xF7
   *  counters, shown in front of the name. `undefined` = this condition has
   *  no counter; `null` = it has one and the device did not report it. The
   *  two are different facts and must not render alike. */
  count?: number | null;
}

export interface FaultGroup {
  title: string;
  cells: FaultCell[];
  /** the raw register(s) behind this column, for the header hover */
  words: string;
}

const ICON: Record<string, ReactNode> = {
  "Over Current": <Zap />, "Charge Over Current": <Zap />,
  "Discharge Over Current": <ZapOff />, "Short Circuit": <Zap />,
  "Blocked Motor": <Ban />, "Position Sensor": <Crosshair />,
  "Over Speed": <Gauge />, "Over Temperature": <Thermometer />,
  "Phase Loss": <ZapOff />, "Anti-Flying": <Wind />,
  "Over Voltage": <TrendingUp />, "Under Voltage": <TrendingDown />,
  "MOSFET Fault": <Cpu />, "Self-Check Error": <ShieldAlert />,
  "Thermal Runaway": <Flame />, "Cell Over Voltage": <TrendingUp />,
  "Cell Under Voltage": <BatteryLow />, "Pack Over Voltage": <TrendingUp />,
  "Pack Under Voltage": <TrendingDown />,
  "Ambient Low Temperature": <ThermometerSnowflake />,
  "Ambient High Temperature": <ThermometerSun />,
  "Charge High Temperature": <ThermometerSun />,
  "Charge Low Temperature": <ThermometerSnowflake />,
  "Discharge Low Temperature": <ThermometerSnowflake />,
  "Discharge MOSFET High Temperature": <ThermometerSun />,
  "Charge MOSFET High Temperature": <ThermometerSun />,
  "Discharge MOSFET Failure": <Cpu />, "Charge MOSFET Failure": <Cpu />,
  "AFE Communication Failure": <Radio />,
  "Throttle Fault1": <Activity />, "Throttle Fault2": <Activity />,
  "Throttle Fault3": <Activity />,
};

const hex = (n: number) => `0x${n.toString(16).padStart(2, "0")}`;
const word = (bf: AssetDataBitfield | undefined) => (bf ? hex(bf.word) : "n/a");
const isOn = (bf: AssetDataBitfield | undefined, name: string) =>
  !!bf?.bits.find((b) => b.name === name && b.on);

/** Straight from one register: every named bit is a cell. */
function fromRegister(title: string, bf: AssetDataBitfield | undefined): FaultGroup {
  if (!bf) {
    return { title, words: "n/a",
      cells: [{ name: "not reported", state: "na",
                hint: `${title}: this device does not send the register` }] };
  }
  return {
    title, words: hex(bf.word),
    cells: bf.bits.map((b) => ({
      name: b.name ?? `Bit ${b.bit}`,
      state: b.on ? (bf.severity === "warning" ? "warn" : "fault") : "clear",
      hint: b.name
        ? `bit ${b.bit}: ${b.name}${b.on ? " -- SET" : ""}`
        : `bit ${b.bit} is set and the firmware has not named it: newer firmware, or a decode error`,
    })),
  };
}

export function vehicleGroups(bits: Record<string, AssetDataBitfield>): FaultGroup[] {
  return [
    fromRegister("Motor", bits.motor_err_bits),
    fromRegister("Controller", bits.mctrl_err_bits),
    fromRegister("Throttle", bits.throttle_err_bits),
  ];
}

/** Protection paired with the alarm that precedes it.
 *
 *  `[protection, alarm]`. A null half means the pack reports only the other
 *  stage for that condition -- not that the stage is clear.
 */
/** protection bit · alarm bit · lifetime counter.
 *
 *  THE COUNTER COLUMN IS DELIBERATELY SPARSE. Only four of the seven 0xF7
 *  counters name a condition this grid shows:
 *
 *    batt_sc_count   "short-circuit faults"          -> Short Circuit
 *    batt_ocd_count  "over-current discharge faults" -> Discharge Over Current
 *    batt_occ_count  "over-current charge faults"    -> Charge Over Current
 *    batt_cuv_count  "cell under-voltage (sleep) events" -> Cell Under Voltage
 *
 *  `batt_ov_count` and `batt_uv_count` are "over-voltage faults" and
 *  "under-voltage faults" in the proto, with NO cell/pack qualifier -- and
 *  this grid has four voltage conditions. Putting them on a guessed row would
 *  tell a technician that a specific cell protection fired N times when the
 *  wire never said which level it counted, so they stay out of the grid and
 *  go in the footer instead. `batt_reset_count` names no condition at all.
 *  Asked of the firmware session; until it answers, the footer is the honest
 *  place. */
const BATTERY: Array<[string, Array<[string | null, string | null, string | null]>]> = [
  ["Voltage", [
    ["Cell Over Voltage", "Cell High Voltage", null],
    ["Cell Under Voltage", "Cell Low Voltage", "batt_cuv_count"],
    ["Pack Over Voltage", "Pack High Voltage", null],
    ["Pack Under Voltage", "Pack Low Voltage", null],
  ]],
  ["Current", [
    ["Charge Over Current", "Charge Over Current", "batt_occ_count"],
    ["Discharge Over Current", "Discharge Current", "batt_ocd_count"],
    ["Short Circuit", null, "batt_sc_count"],
  ]],
  ["Temperature", [
    ["Ambient Low Temperature", "Ambient Low Temperature", null],
    [null, "Ambient High Temperature", null],
    [null, "Charge High Temperature", null],
    [null, "Charge Low Temperature", null],
    [null, "Discharge Low Temperature", null],
    [null, "Discharge MOSFET High Temperature", null],
    [null, "Charge MOSFET High Temperature", null],
  ]],
  ["System", [
    ["Thermal Runaway", null, null],
    ["Discharge MOSFET Failure", null, null],
    ["Charge MOSFET Failure", null, null],
    ["AFE Communication Failure", null, null],
  ]],
];

export function batteryGroups(bits: Record<string, AssetDataBitfield>,
                              counts: Record<string, number | null | undefined> = {},
                             ): FaultGroup[] {
  const err = bits.battery_err_bits, alarm = bits.batt_alarm_bits;
  const words = `err ${word(err)} / alarm ${word(alarm)}`;
  if (!err && !alarm) {
    return [{ title: "Battery", words: "n/a",
      cells: [{ name: "not reported", state: "na",
                hint: "this device sends neither battery_err_bits nor batt_alarm_bits" }] }];
  }
  return BATTERY.map(([title, pairs]) => ({
    title, words,
    cells: pairs.map(([fault, warn, counter]) => {
      const faulted = fault ? isOn(err, fault) : false;
      const warned = warn ? isOn(alarm, warn) : false;
      // THE ESCALATION. Red beats amber: once the pack has acted, saying it is
      // "approaching a limit" understates what happened.
      const state: CellState = faulted ? "fault" : warned ? "warn" : "clear";
      const name = fault ?? warn ?? "";
      const stage = faulted
        ? "PROTECTION ACTIVE: the pack has acted on this"
        : warned
          ? "ALARM: the pack is near this limit but has not acted"
          : "clear";
      const halves = [
        fault ? `protection "${fault}"` : "no protection bit for this condition",
        warn ? `alarm "${warn}"` : "no alarm bit for this condition",
      ].join("; ");
      // `undefined` when this condition HAS no counter, so the cell shows
      // nothing rather than a zero it cannot stand behind.
      const count = counter ? (counts[counter] ?? null) : undefined;
      const tail = counter
        ? ` Lifetime ${counter}: ${count ?? "not reported"}${
            counter === "batt_cuv_count"
              ? " (the pack counts these as sleep events)" : ""}.`
        : "";
      return { name, state, count, hint: `${stage}. ${halves}.${tail}` };
    }),
  }));
}

export function FaultGrid({ title, groups, defaultOpen = false, foot }: {
  title: string;
  groups: FaultGroup[];
  defaultOpen?: boolean;
  /** Anything that belongs INSIDE this dropdown but not on a condition row --
   *  today the three 0xF7 counters that name no single condition. It lives
   *  here rather than in a panel of its own because a counter is a property of
   *  the errors, and a separate "Lifetime trips" panel put the numbers a whole
   *  card away from the conditions they count. */
  foot?: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const all = groups.flatMap((g) => g.cells);
  const faults = all.filter((c) => c.state === "fault").length;
  const warns = all.filter((c) => c.state === "warn").length;
  const tone = faults ? "has-fault" : warns ? "has-warn" : "";
  const count = faults && warns ? `${faults} fault, ${warns} alarm`
    : faults ? `${faults} fault${faults > 1 ? "s" : ""}`
    : warns ? `${warns} alarm${warns > 1 ? "s" : ""}`
    : "all clear";

  return (
    <div className={`lw-faults ${tone}${open ? " open" : ""}`}>
      <button className="lw-fhead" onClick={() => setOpen((o) => !o)}
        title={faults || warns
          ? "Amber is an early warning; red is a protection the pack has acted on."
          : "Every register this device reports reads zero"}>
        <span className="lw-fhint">{groups[0]?.words ?? ""}</span>
        <span className="lw-ftitle">
          <span className="lw-ficon"><TriangleAlert /></span>
          {title}
          <span className="lw-fcount">{count}</span>
        </span>
        <span className="lw-ftoggle"><ChevronDown /></span>
      </button>
      <div className="lw-fbody">
        {groups.map((g) => (
          <div className="lw-fcat" key={g.title}>
            <div className="lw-fcat-t" title={`${g.title} register: ${g.words}`}>{g.title}</div>
            <div className="lw-fgrid">
              {g.cells.map((c) => (
                <div key={c.name} className={`lw-fcell ${c.state}`} title={c.hint}>
                  {ICON[c.name] ?? <TriangleAlert />}
                  {/* IN FRONT OF THE NAME, where Dattatray asked for it: the
                      lifetime count for THIS condition. `undefined` renders
                      nothing (no counter exists), `null` renders a dash (a
                      counter exists and the device did not report it) and a
                      number renders itself -- three distinct facts, because a
                      0 standing in for "not reported" is the one that gets
                      believed. A non-zero count is tinted even when the
                      condition is currently clear: "this has happened before"
                      is the whole reason a lifetime counter exists. */}
                  {c.count !== undefined && (
                    <span className={`lw-fn${c.count ? " hit" : ""}`}>
                      {c.count ?? "—"}
                    </span>
                  )}
                  <span className="lw-fname">{c.name}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {foot}
      <p className="lw-fnote">Amber: the pack is near a limit. Red: it has acted.
        {" "}A number in front of a condition is how many times it has happened
        in this pack's life (0xF7 counters, each wraps 255&nbsp;&rarr;&nbsp;0).</p>
    </div>
  );
}

/** The 0xF7 counters that do NOT name a condition this grid shows.
 *
 *  REPLACED `TripCounts`, which was a separate "Lifetime trips" panel of seven
 *  numbers. Dattatray: *"These were supposed to be numbers inside the battery
 *  errors dropdown in front of each error, and not a separate panel."* Four of
 *  the seven now sit on their own condition row; these three cannot, and the
 *  reason is in `BATTERY`'s comment: the proto calls them "over-voltage
 *  faults" and "under-voltage faults" with no cell/pack qualifier while the
 *  grid shows four voltage conditions, and `batt_reset_count` names no
 *  condition at all. So they stay in the dropdown, labelled as pack-level,
 *  rather than being guessed onto a row.
 */
export function TripFoot({ c }: { c: Record<string, number | null | undefined> }) {
  const ROWS: Array<[string, string, string]> = [
    ["Over voltage", "batt_ov_count",
     "lifetime over-voltage faults. The wire does not say whether it counts "
     + "the CELL or the PACK level, so it is not placed on either row."],
    ["Under voltage", "batt_uv_count",
     "lifetime under-voltage faults. Same ambiguity as over voltage."],
    ["BMS resets", "batt_reset_count",
     "times the BMS has restarted. Not a protection, so it has no condition "
     + "row -- but a climbing count is worth seeing beside the faults."],
  ];
  const any = ROWS.some(([, k]) => c[k] != null);
  if (!any) return null;
  return (
    <div className="lw-ffoot">
      <span className="lw-ffoot-k">Pack-level, not per condition</span>
      {ROWS.map(([label, key, hint]) => (
        <span className="lw-ffoot-i" key={key} title={`${key}: ${hint}`}>
          {label}
          <b className={c[key] ? "hit" : undefined}>{c[key] ?? "—"}</b>
        </span>
      ))}
    </div>
  );
}
